import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerSearch } from '../../hooks/useFileMakerData';
import type { FMCustomer } from '../../types';
import './CustomerSearch.styles.css';

interface CustomerSearchProps {
  value: FMCustomer | null;
  onChange: (customer: FMCustomer | null) => void;
  onDataFill?: (data: FMCustomer) => void;
}

export const CustomerSearch: React.FC<CustomerSearchProps> = ({ 
  value, 
  onChange, 
  onDataFill 
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  
  const { data: customers, isLoading } = useCustomerSearch(searchQuery, showResults && !isSelecting);

  useEffect(() => {
    if (searchQuery.length >= 2 && !value && !isSelecting) {
      setShowResults(true);
    } else if (searchQuery.length < 2 || isSelecting) {
      setShowResults(false);
    }
  }, [searchQuery, value, isSelecting]);

  // Update search query when value changes from parent
  useEffect(() => {
    if (value && !isSelecting) {
      setSearchQuery(value.display_name);
    }
  }, [value, isSelecting]);

  const handleSelectCustomer = (customer: FMCustomer) => {
    setIsSelecting(true);
    setShowResults(false);
    setSearchQuery(customer.display_name);
    
    // Call parent handlers
    onChange(customer);
    if (onDataFill) {
      onDataFill(customer);
    }
    
    // Reset selecting state after a short delay
    setTimeout(() => {
      setIsSelecting(false);
    }, 100);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    
    // Clear selection if user is typing
    if (value && newValue !== value.display_name) {
      onChange(null);
    }
  };

  const handleClearSelection = () => {
    setSearchQuery('');
    onChange(null);
    setShowResults(false);
  };

  return (
    <div className="form-group">
      <label>
        {t('admin.userManagement.form.searchCustomer', 'Rechercher un client FileMaker')}
        <span className="user-form__required">*</span>
      </label>
      <div className="customer-search__input-wrapper">
        <input
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          placeholder={t('admin.userManagement.form.searchCustomerPlaceholder', 
            'Rechercher par nom, email ou ID... (obligatoire)')}
          required
        />
        {value && (
          <button
            type="button"
            className="customer-search__clear-btn"
            onClick={handleClearSelection}
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
      </div>
      
      {isLoading && (
        <div className="customer-search__loading">
          {t('admin.userManagement.messages.loading', 'Recherche en cours...')}
        </div>
      )}
      
      {showResults && customers && customers.length > 0 && (
        <div className="customer-search__results">
          {customers.map((customer: FMCustomer) => (
            <div
              key={customer.fm_client_id}
              className={`customer-search__item ${
                value?.fm_client_id === customer.fm_client_id ?
                'customer-search__item--selected' : ''
              }`}
              onClick={() => handleSelectCustomer(customer)}
            >
              <div className="customer-search__name">
                {customer.display_name} (ID: {customer.fm_client_id})
              </div>
              {customer.email && (
                <div className="customer-search__email">
                  {customer.email}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {value && (
        <div className="customer-selected">
          {t('admin.userManagement.form.selectedCustomer', 'Client sélectionné')}: {value.display_name} (ID: {value.fm_client_id})
        </div>
      )}
    </div>
  );
};